/* eslint-disable max-lines */
import { EventBus, Events } from "jiffy-store";
import { writeCookie } from "common/utils/cookie";
import SlidePanel from "components/jiffy/masthead-redesign2025/slide-panel/slide-panel";
import LocalDeliveryModal from "components/jiffy/masthead-redesign2025/local-delivery/local-delivery-modal/local-delivery-modal";
import LocalDeliveryToggleClass from "components/jiffy/masthead-redesign2025/local-delivery/local-delivery-toggle/local-delivery-toggle-class";
import SmartyStreetsAutocomplete from "components/jiffy/masthead-redesign2025/local-delivery/smarty-streets-autocomplete/smarty-streets-autocomplete";
import AddressSaver from "components/jiffy/masthead-redesign2025/local-delivery/address-saver/address-saver";
import AddressList from "components/jiffy/masthead-redesign2025/local-delivery/address-list/address-list";
import { sleep } from "common/utils/helpers/delay";
import AddressEditorManager from "components/jiffy/masthead-redesign2025/local-delivery/address-editor";
import UserContextHelper from "components/jiffy/masthead-redesign2025/local-delivery/utils/user-context-helper";
import { handlePDPAddressUpdate } from "components/jiffy/masthead-redesign2025/local-delivery/utils/zipcode-helper";
import "components/jiffy/masthead-redesign2025/local-delivery/local-delivery.scss";

const DELAY_MS = 100;

const SAVE_ADDRESS_LOADING_IS_VISIBLE_CLASS =
  "smarty-streets-autocomplete__save-address-loading--is-visible";

const SELECTORS = {
  autocomplete: ".js-smarty-streets-autocomplete",
  saveAddressLoading: ".js-smarty-streets-autocomplete-save-address-loading"
};

/**
 * @class LocalDelivery
 *
 * Manages the local delivery address collection flow.
 * Handles the transition between the initial modal and the slide panel
 * that contains the SmartyStreets autocomplete interface.
 *
 * @example
 * const localDelivery = new LocalDelivery();
 * localDelivery.init();
 */
class LocalDelivery {
  /** @type {LocalDeliveryModal} */
  static #localDeliveryModal = null;

  /** @type {SlidePanel} */
  static #localDeliverySlidePanel = null;

  /** @type {AddressSaver} */
  static #addressSaver = null;

  /** @type {SmartyStreetsAutocomplete} */
  static #smartyStreetsAutocomplete = null;

  /** @type {AddressList} */
  static #addressList = null;

  /** @type {boolean} */
  static #initialized = false;

  /** @type {LocalDeliveryToggleClass} */
  static #toggle = null;

  /**
   * Gets the slide panel element
   * @private
   * @returns {HTMLElement|null} The slide panel element
   */
  static get #$slidePanelAddressList() {
    return document.querySelector(".js-slide-panel[data-panel-id='local-delivery-slide-panel']");
  }

  /**
   * Gets the save address loading element
   * @returns {Element} The save address loading element
   * @private
   */
  static get #$saveAddressLoading() {
    return document.querySelector(SELECTORS.saveAddressLoading);
  }

  /**
   * initialize the local delivery modal and bind events
   * @public
   */
  static init() {
    LocalDelivery.#initToggle();

    if (LocalDelivery.#initialized) return;
    LocalDelivery.#initialized = true;

    LocalDelivery.#showModalIfNeeded();
    LocalDelivery.#bindEvents();
    AddressEditorManager.init();
  }

  /**
   * Initialize the local delivery toggle to read cookies and check eligibility
   * @private
   */
  static #initToggle() {
    LocalDelivery.#toggle = new LocalDeliveryToggleClass();
  }

  /**
   * Updates the address cookie and refreshes the toggle display
   * @param {Object} address - Address object with address1, city, state, and zipcode
   * @public
   */
  static updateAddressAndRefresh(address) {
    if (!address) return;

    const { address1, city, zipcode } = address;
    if (!city || !zipcode) return;

    // Format address cookie as "address1,+city+zipcode" (matching server format)
    // Example: "325+N+Gray+St,+Indianapolis+46201" displays as "325 N Gray St, Indianapolis 46201"
    const addressParts = [];
    if (address1) {
      // Attach comma to address1 to avoid extra space
      addressParts.push(`${address1},`);
    }
    addressParts.push(city);
    addressParts.push(zipcode);

    const addressCookieValue = addressParts.join("+");
    // write cookie for 4 hours
    writeCookie("address", addressCookieValue, 14400, true);

    // Note: full_address cookie is set server-side, so we leave it as is
    // The toggle will read it and update accordingly

    // Ensure toggle is initialized before calling init()
    if (!LocalDelivery.#toggle) {
      LocalDelivery.#initToggle();
    }
    LocalDelivery.#toggle?.init();
  }

  /**
   * Binds global event listeners for local delivery workflow.
   * Using static method to ensure single event listener registration
   * regardless of component instantiation count.
   * @private
   */
  static #bindEvents() {
    EventBus.on(
      Events.SHOW_SS_AUTOCOMPLETE_PANEL,
      LocalDelivery.#handleShowLocalDeliverySlidePanel
    );

    EventBus.on(
      Events.SS_AUTOCOMPLETE_ADDRESS_CLEARED,
      LocalDelivery.#handleSmartyStreetsAutocompleteCleared
    );

    EventBus.on(Events.LOCAL_DELIVERY_ADDRESS_UPDATED, LocalDelivery.#handleAddressUpdated);
  }

  /**
   * clear errors from the address saver when the SmartyStreets autocomplete is cleared
   * @private
   */
  static #handleSmartyStreetsAutocompleteCleared() {
    LocalDelivery.#addressSaver?.clearErrors();
  }

  /**
   * Handles address update event from AddressList
   * Updates the address cookie and refreshes the toggle display
   * @private
   * @param {Object} event - Event object with target property containing the address
   */
  static #handleAddressUpdated(event) {
    LocalDelivery.updateAddressAndRefresh(event.target);
  }

  /**
   * Conditionally displays the local delivery modal based on user session state.
   * @private
   */
  static #showModalIfNeeded() {
    LocalDelivery.#localDeliveryModal = new LocalDeliveryModal();

    if (LocalDelivery.#localDeliveryModal.shouldShowModal()) {
      LocalDelivery.#localDeliveryModal.showModal();
    }
  }

  /**
   * initialize the SmartyStreetsAutocomplete and AddressSaver
   * hide the local delivery modal
   * open the local delivery autocomplete slide panel
   * @private
   */
  static #handleShowLocalDeliverySlidePanel() {
    LocalDelivery.#localDeliveryModal.hideModal(null, false);

    LocalDelivery.#localDeliverySlidePanel = new SlidePanel({
      panelId: "local-delivery-slide-panel",
      closeOnBackdrop: false,
      closeOnEscape: false,
      onOpen: async () => {
        await LocalDelivery.#initComponents();
      },
      trackDismissal: true,
      mixpanelEvent: "address_collection_modal_dismissed",
      mixpanelProperties: {
        zipcode: UserContextHelper.getSessionZipcode(),
        source: "address_list_modal"
      }
    });

    LocalDelivery.#localDeliverySlidePanel.open();
  }

  /**
   * Initializes all components and connects them together
   * @private
   */
  static async #initComponents() {
    // wait for the DOM to be ready
    await sleep(DELAY_MS);

    // Initialize AddressList first to fetch saved addresses
    LocalDelivery.#addressList = new AddressList();
    LocalDelivery.#addressList.setSlidePanel(LocalDelivery.#localDeliverySlidePanel);
    await LocalDelivery.#addressList.init();

    // Get saved addresses for the autocomplete
    const savedAddresses = LocalDelivery.#addressList.addresses;

    // Initialize SmartyStreetsAutocomplete with saved addresses
    LocalDelivery.#smartyStreetsAutocomplete = new SmartyStreetsAutocomplete(savedAddresses);

    // Initialize AddressSaver with callbacks
    LocalDelivery.#addressSaver = new AddressSaver({
      errorContainerSelector: SELECTORS.autocomplete,
      onSuccess: LocalDelivery.#handleAddressSaved,
      onLoadingChange: LocalDelivery.#handleSaveAddressLoadingChange
    });

    // Handle address selection - either select saved address or save new one
    LocalDelivery.#smartyStreetsAutocomplete.onSubmitCallback(async result => {
      if (result.isSaved && result.savedAddressId != null) {
        // Reuse existing address selection logic from AddressList
        // No validation needed for saved addresses
        await LocalDelivery.#addressList?.selectAddress(result.savedAddressId);
      } else {
        // Save new address (will trigger validation)
        await LocalDelivery.#addressSaver?.saveAddress(result);
      }
    });
  }

  /**
   * handle the event when the address is saved
   * close the local delivery slide panel
   * hide the local delivery modal
   * @private
   */
  static async #handleAddressSaved(savedAddress) {
    // Track validation completion
    LocalDelivery.#smartyStreetsAutocomplete?.trackValidationCompleted({
      isLocalDeliveryEligible: true
    });

    await LocalDelivery.#addressList?.selectAddress(savedAddress?.id);

    const pdpHandled = await handlePDPAddressUpdate(savedAddress, async () => {
      await LocalDelivery.#addressList?.init();
      LocalDelivery.#smartyStreetsAutocomplete?.clearInput();
      LocalDelivery.#addressSaver.setLoading(false);
      LocalDelivery.#localDeliverySlidePanel.close();
      LocalDelivery.#localDeliveryModal.hideModal();
    });

    if (pdpHandled) return;

    if (LocalDelivery.#$slidePanelAddressList?.dataset.pageReload === "true") {
      window.location.reload();
    } else {
      LocalDelivery.#handleSaveAddressLoadingChange(false);
      LocalDelivery.#smartyStreetsAutocomplete?.clearInput();
      LocalDelivery.#addressSaver.setLoading(false);
      EventBus.publish(Events.LOCAL_DELIVERY_ADDRESS_UPDATED, savedAddress);
    }
  }

  /**
   * show/hide the save address loading element based on the isLoading parameter
   * @private
   */
  static #handleSaveAddressLoadingChange(isLoading) {
    LocalDelivery.#$saveAddressLoading?.classList?.toggle(
      SAVE_ADDRESS_LOADING_IS_VISIBLE_CLASS,
      isLoading
    );
  }
}

export default LocalDelivery;
